/**
 * useProjectLoading Hook
 * Extracted from MultiProjectContext.tsx
 * Handles loading available projects and default project creation
 */

import { useCallback, useEffect, useRef } from 'react';
import { projectService, ProjectInfo } from '@/features/projects/services/projectService';
import { SYNTHETIC_DEFAULT_PROJECT_ID } from '@/features/projects/utils/syntheticDefaultProject';
import { transformProjectInfoFromBackend } from '@/shared/utils/fieldNameTransform';
import { log } from '@/shared/utils/logger';
import {
  loadSavedProjectCatalog,
  loadSavedProjectStateUserId,
  persistProjectCatalog,
  type ProjectPersistenceStorageScope,
} from './useProjectPersistence';

// Catalog load outcomes published through MultiProjectContext.error. The project filter
// shows a translated message with a retry for the first two; the transient state retries itself.
export const PROJECT_CATALOG_ERRORS = {
  failed: 'Failed to load projects',
  unauthorized: 'Authentication required to load projects',
  transient: 'Waiting for authenticated session',
} as const;

interface UseProjectLoadingParams {
  user: { id?: string; role?: string } | null;
  persistenceUserId?: string | null;
  storageScope?: ProjectPersistenceStorageScope;
  hasInitialized: boolean;
  userHasPreferences: boolean;
  selectedProjectIds: string[];
  setAvailableProjects: (projects: ProjectInfo[]) => void;
  setSelectedProjectIds: (ids: string[]) => void;
  setVisibleProjectIds: (ids: Set<string>) => void;
  setHasInitialized: (value: boolean) => void;
  setIsLoading: (value: boolean) => void;
  setError: (error: string | null) => void;
}

export function useProjectLoading({
  user,
  persistenceUserId,
  storageScope = 'local',
  hasInitialized,
  userHasPreferences,
  selectedProjectIds,
  setAvailableProjects,
  setSelectedProjectIds,
  setVisibleProjectIds,
  setHasInitialized,
  setIsLoading,
  setError
}: UseProjectLoadingParams) {
  const hasInitializedRef = useRef(hasInitialized);
  const userHasPreferencesRef = useRef(userHasPreferences);
  const selectedProjectIdsRef = useRef(selectedProjectIds);
  const latestLoadRequestIdRef = useRef(0);
  const mountedRef = useRef(true);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    hasInitializedRef.current = hasInitialized;
  }, [hasInitialized]);

  useEffect(() => {
    userHasPreferencesRef.current = userHasPreferences;
  }, [userHasPreferences]);

  useEffect(() => {
    selectedProjectIdsRef.current = selectedProjectIds;
  }, [selectedProjectIds]);

  const loadProjects = useCallback(async () => {
    if (!user) return;

    const requestId = latestLoadRequestIdRef.current + 1;
    latestLoadRequestIdRef.current = requestId;

    const currentUserId = typeof user.id === 'string' ? user.id : null;
    const currentUserRole = typeof user.role === 'string' ? user.role.toLowerCase() : null;
    const shouldRequireVisibleProject = currentUserRole === 'client' || currentUserRole === 'partner';
    const storageOptions = { userId: persistenceUserId || currentUserId, storageScope };
    const savedStateUserId = loadSavedProjectStateUserId(storageOptions);
    const isDifferentSavedUser = Boolean(savedStateUserId && currentUserId && savedStateUserId !== currentUserId);
    const selectedProjectIdsAtRequestStart = selectedProjectIdsRef.current;
    const isLatestRequest = () => latestLoadRequestIdRef.current == requestId;

    setIsLoading(true);
    setError(null);

    try {
      const { projects, status } = await projectService.listProjectsWithStatus();
    if (!isLatestRequest() || !mountedRef.current) {
      log.info('MultiProject', 'Ignoring stale project catalog response', { requestId });
      return;
    }

      if (status !== 'ok') {
        const savedCatalog = loadSavedProjectCatalog(storageOptions);
        if (savedCatalog.length > 0) {
          setAvailableProjects(savedCatalog);
        }

        if (status === 'transient') {
          log.warn('MultiProject', 'Project catalog load deferred until auth/session recovers');
          setError(PROJECT_CATALOG_ERRORS.transient);
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }
          retryTimeoutRef.current = setTimeout(() => {
            if (!mountedRef.current) {
              return;
            }
            if (latestLoadRequestIdRef.current === requestId) {
              void loadProjects();
            }
          }, 1000);
        } else {
          log.warn('MultiProject', 'Project catalog load blocked by unauthorized session state');
          setError(PROJECT_CATALOG_ERRORS.unauthorized);
        }
        return;
      }

      // A user can change selection while the catalog request is in flight.
      // Reconcile the current intent, not the snapshot from request start.
      const currentHasInitialized = hasInitializedRef.current;
      const currentUserHasPreferences = !isDifferentSavedUser && userHasPreferencesRef.current;
      const currentSelectedProjectIds = isDifferentSavedUser ? [] : selectedProjectIdsRef.current;
      const selectionChangedWhileLoading = currentSelectedProjectIds.length !== selectedProjectIdsAtRequestStart.length
        || currentSelectedProjectIds.some((id, index) => id !== selectedProjectIdsAtRequestStart[index]);

      // If no projects exist, create a default project entry
      if (projects.length === 0) {
        const defaultProject: ProjectInfo = {
          projectId: SYNTHETIC_DEFAULT_PROJECT_ID,
          projectName: 'Default Project',
          // i18n-raw-ui-literal-exempt: Persisted first-run project description; never localize stored user data.
          description: 'Default workspace',
          role: 'owner',
          lastAccessed: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          nodeCount: 0,
          isFavorite: false,
          isPinned: false,
          status: 'active'
        };
        setAvailableProjects([defaultProject]);
        persistProjectCatalog([defaultProject], storageOptions);

        // Only auto-select for first-time users (no saved preferences)
        if (!currentHasInitialized && !currentUserHasPreferences) {
          log.info('MultiProject', 'First-time user detected, auto-selecting default project');
          setSelectedProjectIds([SYNTHETIC_DEFAULT_PROJECT_ID]);
          setVisibleProjectIds(new Set([SYNTHETIC_DEFAULT_PROJECT_ID]));
        } else if (!currentHasInitialized && currentUserHasPreferences) {
          log.info('MultiProject', 'User has preferences, keeping saved state:', currentSelectedProjectIds);
        }
        setHasInitialized(true);
      } else {
        // Transform project data to ensure consistent field names
        const transformedProjects = projects.map(transformProjectInfoFromBackend);
        const effectiveProjects = transformedProjects;

        setAvailableProjects(effectiveProjects);
        persistProjectCatalog(effectiveProjects, storageOptions);
        const validSelections = currentSelectedProjectIds.filter(id =>
          effectiveProjects.some(p => p.projectId === id)
        );

        // Auto-selection logic with validation
        if (!currentHasInitialized) {
          if (validSelections.length > 0) {
            setSelectedProjectIds(validSelections);
            setVisibleProjectIds(new Set(validSelections));
          } else if (selectionChangedWhileLoading && currentSelectedProjectIds.length === 0 && !shouldRequireVisibleProject) {
            setSelectedProjectIds([]);
            setVisibleProjectIds(new Set());
          } else if (!currentUserHasPreferences && effectiveProjects.length > 0) {
            // First time user - auto-select first project
            const firstProjectId = effectiveProjects[0].projectId;
            log.info(
              'MultiProject',
              'First-time user detected, auto-selecting first project:',
              firstProjectId
            );
            setSelectedProjectIds([firstProjectId]);
            setVisibleProjectIds(new Set([firstProjectId]));
          } else if (currentUserHasPreferences) {
            if (currentSelectedProjectIds.length > 0 && effectiveProjects.length > 0) {
              const firstProjectId = effectiveProjects[0].projectId;
              log.info('MultiProject', 'Saved project selections are no longer accessible, selecting first available project:', firstProjectId);
              setSelectedProjectIds([firstProjectId]);
              setVisibleProjectIds(new Set([firstProjectId]));
            } else if (shouldRequireVisibleProject && effectiveProjects.length > 0) {
              const firstProjectId = effectiveProjects[0].projectId;
              log.info('MultiProject', 'External account has accessible projects, selecting first available project:', firstProjectId);
              setSelectedProjectIds([firstProjectId]);
              setVisibleProjectIds(new Set([firstProjectId]));
            } else {
              log.info('MultiProject', 'User prefers no projects selected, keeping empty state');
              setSelectedProjectIds([]);
              setVisibleProjectIds(new Set());
            }
          }
        } else {
          const selectionsChanged =
            validSelections.length !== currentSelectedProjectIds.length ||
            validSelections.some((id, index) => id !== currentSelectedProjectIds[index]);

          if (selectionsChanged) {
            log.info('MultiProject', 'Reconciling selected projects after access change:', {
              previous: currentSelectedProjectIds,
              next: validSelections,
            });
            setSelectedProjectIds(validSelections);
            setVisibleProjectIds(new Set(validSelections));
          }
        }
        setHasInitialized(true);
      }
    } catch (err) {
      if (!isLatestRequest() || !mountedRef.current) {
        log.info('MultiProject', 'Ignoring stale project catalog error response', { requestId });
        return;
      }
      log.error('MultiProject', 'Failed to load projects:', err);
      setError(PROJECT_CATALOG_ERRORS.failed);
      const currentUserId = typeof user?.id === 'string' ? user.id : null;
      const savedCatalog = loadSavedProjectCatalog({ userId: persistenceUserId || currentUserId, storageScope });
      if (savedCatalog.length > 0) {
        setAvailableProjects(savedCatalog);
      }
    } finally {
      if (isLatestRequest() && mountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [
    user,
    persistenceUserId,
    storageScope,
    setAvailableProjects,
    setSelectedProjectIds,
    setVisibleProjectIds,
    setHasInitialized,
    setIsLoading,
    setError
  ]);

  return { loadProjects };
}
